/* One review in full: its summary, every finding it stored, and how it got there.

   Findings suppressed by strictness are stored too and listed here, because the question
   an administrator actually asks is "what would raising strictness surface". */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { api } from "@/api";
import { useOrgPath } from "@/session";
import { useApi } from "@/useApi";
import { Async, Panel, Severity, StateDot, duration, panel, relativeTime } from "@/ui";

const VOTES = [
  ["helpful", "Helpful"],
  ["not_helpful", "Not helpful"],
];

export function PullDetail() {
  const { jobId } = useParams();
  const orgPath = useOrgPath();
  const review = useApi(
    (signal) => api.get(`${orgPath}/pulls/${jobId}`, { signal }),
    [orgPath, jobId],
    { skip: !orgPath },
  );

  return (
    <Async state={review}>
      {(data) => (
        <>
          <Panel>
            <Link to="/pulls" style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>
              ← All pull requests
            </Link>
            <h1 style={{ font: "var(--text-h3)", color: "var(--text-primary)", margin: "10px 0 6px" }}>
              {data.title || "Untitled"}{" "}
              <span style={{ color: "var(--text-disabled)" }}>#{data.number}</span>
            </h1>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "18px",
                font: "var(--text-body-14)",
                color: "var(--text-muted)",
              }}
            >
              <span>{data.repository}</span>
              <span>{data.author || "unknown author"}</span>
              <StateDot state={data.state} />
              <span>{relativeTime(data.finishedAt ?? data.createdAt)}</span>
              {data.confidence !== null && <span>Merge confidence {data.confidence}/5</span>}
              {data.durationMs !== null && <span>Reviewed in {duration(data.durationMs)}</span>}
              {data.strictness && <span>Strictness: {data.strictness}</span>}
            </div>
            {data.skipReason && (
              <p
                style={{
                  font: "var(--text-body-14)",
                  color: "var(--text-muted)",
                  marginTop: "14px",
                }}
              >
                Not reviewed: <code>{data.skipReason}</code>. That is a trigger rule this
                organization configured, not a failure.
              </p>
            )}
            {data.failureReason && (
              <p
                style={{ font: "var(--text-body-14)", color: "var(--danger)", marginTop: "14px" }}
              >
                Review failed: <code>{data.failureReason}</code>
              </p>
            )}
          </Panel>

          {data.summary && (
            <Panel title="Summary">
              <p
                style={{
                  font: "var(--text-body-14)",
                  color: "var(--text-secondary)",
                  whiteSpace: "pre-wrap",
                  margin: 0,
                }}
              >
                {data.summary}
              </p>
              {data.confidenceRationale && (
                <p
                  style={{
                    font: "var(--text-caption)",
                    color: "var(--text-muted)",
                    marginTop: "10px",
                  }}
                >
                  {data.confidenceRationale}
                </p>
              )}
            </Panel>
          )}

          <Panel title={`Findings (${data.findings.length})`}>
            {data.findings.length === 0 ? (
              <p style={{ font: "var(--text-body-14)", color: "var(--text-disabled)" }}>
                No findings were recorded for this review.
              </p>
            ) : (
              <div style={{ display: "grid", gap: "12px" }}>
                {data.findings.map((finding) => (
                  <Finding
                    key={finding.id}
                    finding={finding}
                    orgPath={orgPath}
                    jobId={jobId}
                    pullNumber={data.number}
                    headSha={data.headSha}
                  />
                ))}
              </div>
            )}
          </Panel>

          {data.timeline.length > 0 && (
            <Panel title="Timeline">
              <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "8px" }}>
                {data.timeline.map((event, index) => (
                  <li
                    key={`${event.type}-${index}`}
                    style={{
                      display: "flex",
                      gap: "12px",
                      font: "var(--text-body-14)",
                      color: "var(--text-secondary)",
                    }}
                  >
                    <span style={{ color: "var(--text-disabled)", minWidth: "120px" }}>
                      {relativeTime(event.at)}
                    </span>
                    <span>{event.type.replace(/_/g, " ")}</span>
                    {event.severity && <Severity level={event.severity} />}
                  </li>
                ))}
              </ol>
            </Panel>
          )}
        </>
      )}
    </Async>
  );
}

function Finding({ finding, orgPath, jobId, pullNumber, headSha }) {
  const [vote, setVote] = useState(finding.myVote);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function cast(value) {
    setBusy(true);
    setError("");
    try {
      await api.post(
        `${orgPath}/pulls/${jobId}/findings/${encodeURIComponent(finding.fingerprint)}/feedback`,
        { pullNumber, headSha, value },
      );
      setVote(value);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article style={{ ...panel, padding: "16px", background: "var(--surface-elevated)" }}>
      <header style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <Severity level={finding.severity} />
        <strong style={{ font: "var(--text-body-14)", color: "var(--text-primary)" }}>
          {finding.title}
        </strong>
        <code style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>
          {finding.path}:{finding.startLine}
        </code>
        <span style={{ font: "var(--text-caption)", color: "var(--text-disabled)" }}>
          {finding.commentType}
        </span>
      </header>
      <p
        style={{
          font: "var(--text-body-14)",
          color: "var(--text-secondary)",
          whiteSpace: "pre-wrap",
          margin: "10px 0 0",
        }}
      >
        {finding.body}
      </p>
      {finding.suggestion && (
        <pre
          style={{
            font: "var(--text-caption)",
            fontFamily: "var(--font-mono)",
            color: "var(--text-primary)",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            padding: "10px",
            overflowX: "auto",
            marginTop: "10px",
          }}
        >
          {finding.suggestion}
        </pre>
      )}
      <footer style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px" }}>
        {VOTES.map(([value, label]) => (
          <button
            key={value}
            type="button"
            disabled={busy}
            onClick={() => cast(value)}
            style={{
              font: "var(--text-caption)",
              padding: "5px 12px",
              cursor: busy ? "wait" : "pointer",
              borderRadius: "var(--radius-pill)",
              color: vote === value ? "var(--control-on-ink)" : "var(--text-muted)",
              background: vote === value ? "var(--control-on-bg)" : "transparent",
              border: `1px solid ${vote === value ? "var(--control-on-border)" : "var(--border)"}`,
            }}
          >
            {label}
          </button>
        ))}
        {error && (
          <span style={{ font: "var(--text-caption)", color: "var(--danger)" }}>{error}</span>
        )}
      </footer>
    </article>
  );
}
